import { frontOverhangAllowed } from "@CommonUtils/pog/frontOverhang";
import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import { Pog } from "./pog";
import { PlanogramShelf } from "./shelf";
import {
  getMergedDeadSpans,
  getShelfItemWidth,
  getShelfItemXOffsets,
} from "@CommonUtils/pog/shelfMerchandising";
import { getShelfItemDividerSlots } from "@CommonUtils/pog/shelfDividers";
import { usableShelfDepth } from "@CommonUtils/pog/shelfLoader";
import { getOrientedDimensions } from "@CommonUtils/pog/orientedDimensions";
import { getAppliedSqueezeRelief } from "@CommonUtils/pog/squeezeRelief";
import { uuid } from "@CommonUtils/uuid";
import { BaseProduct } from "@CommonTypes/merchflow/pog/product";

export type PlanogramItemOrientation = "FRONT" | "FRONT2" | "SIDE" | "SIDE2" | "TOP" | "TOP2";

/** The fixture type a product is intended to merchandise on, mirroring the backend `shelf_type` enum. */
export type ItemShelfType = "BASKET" | "HANGCELL" | "PEGBOARD" | "REGULAR" | "RISER";

export type PlanogramItemUpdateStatus = {
  operation?: string;
  changeType?: string;
};

/** Whole boxes of size `box` along `span`; a cap that is there holds at least one. */
const fitAtLeastOne = (span: number, box: number) =>
  box > 0 ? Math.max(1, Math.floor(span / box + 1e-9)) : 1;

/**
 * The placement a saved cap count was counted on: the cap, the pack's orientation and style, and
 * the stack the cap stands beside or lies on. Editing any of it leaves the count stale.
 */
const capGeometryOf = (item: PlanogramItemCommon, axis: "x" | "y") =>
  [
    axis === "x" ? item.xCapNum : item.yCapNum,
    axis === "x" ? item.xCapOrientation : item.yCapOrientation,
    item.orientation,
    item.merchandisingStyle,
    item.facings,
    item.facingsRows,
  ].join("|");

type PlanogramItemPlacementFields = {
  orientation?: PlanogramItemOrientation | null;
  validOrientations?: PlanogramItemOrientation[] | null;
  facings?: number | null;
  facingsRows?: number | null;
};

export const normalizePlanogramItemPlacementDefaults = <T extends PlanogramItemPlacementFields>(
  item: T,
): T & {
  orientation: PlanogramItemOrientation;
  validOrientations: PlanogramItemOrientation[];
  facings: number;
  facingsRows: number;
} => {
  const validOrientations = item.validOrientations ?? ["FRONT"];
  const facingsRows = item.facingsRows ?? 1;

  return {
    ...item,
    orientation: item.orientation ?? validOrientations[0] ?? "FRONT",
    validOrientations: [...validOrientations],
    facingsRows,
    facings: item.facings ?? facingsRows,
  };
};

/** The fields a pack's measurements are read from, before orientation. */
export type PlanogramItemDimensionFields = Pick<
  PlanogramItemCommonNonCircular,
  | "merchandisingStyle"
  | "unitWidth"
  | "unitHeight"
  | "unitDepth"
  | "trayWidth"
  | "trayHeight"
  | "trayDepth"
  | "caseWidth"
  | "caseHeight"
  | "caseDepth"
  | "squeezeWidth"
  | "squeezeHeight"
  | "squeezeDepth"
  | "squeezeWidthPct"
  | "squeezeHeightPct"
  | "squeezeDepthPct"
>;

const squeezeItemDimension = (
  result: number,
  squeeze: boolean,
  squeezePercentage: number,
  relief: number,
) => (squeeze ? result * (squeezePercentage + relief * (1 - squeezePercentage)) : result);

/**
 * One pack as the planogram merchandises it: the tray or case when the item is
 * merchandised that way (else the unit), squeezed, not yet oriented. The one
 * copy behind `PlanogramItem.getWidth` / `getHeight` / `getDepth`.
 */
export const getPlanogramItemSqueezedDimensions = (item: PlanogramItemDimensionFields) => {
  const width =
    (item.merchandisingStyle === "TRAY" && item.trayWidth) ||
    (item.merchandisingStyle === "CASE" && item.caseWidth) ||
    item.unitWidth;
  const height =
    (item.merchandisingStyle === "TRAY" && item.trayHeight) ||
    (item.merchandisingStyle === "CASE" && item.caseHeight) ||
    item.unitHeight;
  const depth =
    (item.merchandisingStyle === "TRAY" && item.trayDepth) ||
    (item.merchandisingStyle === "CASE" && item.caseDepth) ||
    item.unitDepth;
  return {
    width: squeezeItemDimension(
      width,
      item.squeezeWidth,
      item.squeezeWidthPct,
      getAppliedSqueezeRelief(item, "width"),
    ),
    height: squeezeItemDimension(
      height,
      item.squeezeHeight,
      item.squeezeHeightPct,
      getAppliedSqueezeRelief(item, "height"),
    ),
    depth: squeezeItemDimension(
      depth,
      item.squeezeDepth,
      item.squeezeDepthPct,
      getAppliedSqueezeRelief(item, "depth"),
    ),
  };
};

/**
 * One facing's width as the layout draws it (`PlanogramItem.getWidth`, the
 * step `getShelfItemDividerSlots` lays facings at), read from a stored item.
 */
export const getPlanogramItemFacingWidth = (
  item: PlanogramItemDimensionFields & PlanogramItemPlacementFields,
): number =>
  getOrientedDimensions(
    getPlanogramItemSqueezedDimensions(item),
    normalizePlanogramItemPlacementDefaults(item).orientation,
  ).width;

/** What the flow's divider policy said about this product when the pog was generated. */
export interface ItemDividerNeed {
  needed: boolean;
  /** Text stored by older pogs ("Form is Spray"); new pogs leave it empty and set `reasonValue`. */
  reason: string;
  /** A legacy attribute / CDT rule's reason; absent for the base and ticked sources. */
  reasonValue?: ItemDividerReasonValue | null;
}

/** The product's value on the rule's attribute or CDT level, for the frontend to word. */
export interface ItemDividerReasonValue {
  kind: "attribute" | "cdt";
  /** The attribute key ("form") or the CDT level ("cdt1"). */
  field: string;
  /** Empty: the product has no value there. */
  value: string;
}

/** A code, not copy: the frontend words it. */
export type ItemLoaderNeedReason =
  | "on-loader-shelf-in-base"
  | "not-on-loader-shelf-in-base"
  | "new-to-base"
  | "ticked"
  | "not-ticked";

export interface ItemLoaderNeed {
  needed: boolean;
  reason: ItemLoaderNeedReason;
}

export interface PlanogramItemCommonNonCircular extends BaseProduct {
  orientation: PlanogramItemOrientation;
  validOrientations: PlanogramItemOrientation[];
  merchandisingStyle: "UNIT" | "TRAY" | "CASE";
  /** Legacy metadata only; overhang eligibility always uses the current merchandising style. */
  overhangAsUnit?: boolean;
  /** Optional in the contract until the backend adapter populates it from the product's `shelf_type`. */
  shelfType?: ItemShelfType;
  trayWidth: number;
  trayHeight: number;
  trayDepth: number;
  caseWidth: number;
  caseHeight: number;
  caseDepth: number;
  displayWidth: number;
  displayHeight: number;
  displayDepth: number;
  dividerWidth: number;
  dividerHeight: number;
  noOfUnitsInCase: number;
  unitsCaseWide: number;
  unitsCaseDeep: number;
  unitsCaseHigh: number;
  noOfUnitsInTray: number;
  unitsTrayWide: number;
  unitsTrayDeep: number;
  unitsTrayHigh: number;
  overhangSqueezeDepthFlag: boolean;
  facingsRows: number;
  minFacingsRows: number;
  maxFacingsRows: number;
  /**
   * True only when a source file stated the maximum (PSA `merchYMax`, carried
   * as-is into `maxFacingsRows`). Absent: legacy and generated pogs export none.
   */
  maxFacingsRowsStated?: boolean;
  /** PSA x/y/z nesting in the product's own frame: stored for the round trip, not applied to geometry. */
  nestingWidth?: number;
  nestingHeight?: number;
  nestingDepth?: number;
  /**
   * Depth-facing count stated by the source planogram (PSA `d_facings`), or
   * null when the source did not state one. When null, `getDepthFacings()`
   * falls back to the geometric `floor(usable shelf depth / item depth)`
   * guess — exactly the pre-3.7 behaviour on a shelf without a spring loader. Optional in data contracts (pogs older
   * than 3.7 lack it), normalized to null in the class.
   */
  unitsDeep?: number | null;
  /** Optional in data contracts (pogs older than 4.4 lack it), null in the class. */
  dividerNeed?: ItemDividerNeed | null;
  /** Null when no loader rule judged this item. */
  loaderNeed?: ItemLoaderNeed | null;
  zCappingFlag: boolean;
  /**
   * Loose units pogger placed behind the stock (its `merchZCaps` total, across
   * all facings); null means derive geometrically, as on the base-pog fill
   * path. A count, unlike `zCapNum`, which is a cap depth.
   */
  zCappedUnits?: number | null;
  /**
   * Units pogger placed in the x- and y-caps (its `xCapping` / `yCapping` `units`, after any weight
   * trim); null means count the caps geometrically, as for an imported PSA.
   */
  xCappedUnits?: number | null;
  yCappedUnits?: number | null;
  /** Optional in data contracts, but initialized with default values in the class */
  xCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  xCapOrientation?: PlanogramItemOrientation;
  /** Optional in data contracts, but initialized with default values in the class */
  yCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  yCapOrientation?: PlanogramItemOrientation;
  /** Optional in data contracts, but initialized with default values in the class */
  zCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  zCapOrientation?: PlanogramItemOrientation;
  updateStatus?: PlanogramItemUpdateStatus;
}

export class PlanogramItemCommon implements PlanogramItemCommonNonCircular {
  uniqueId: string;
  id: string;
  productCode: string;
  name: string;
  upc: string;
  brand: string;
  variant: string;
  categoryCode: string;
  subCategory: string;
  size: string;
  unitWeight?: number | null;
  trayWeight?: number | null;
  caseWeight?: number | null;
  color: string;
  /** The unit `unitWeight` / tray / case are stated in. Null means unknown, never pounds. */
  weightUom: string | null;
  profit: number | null;
  quantity: number;
  price: number;
  salesAmount: number;
  orientation: PlanogramItemOrientation;
  validOrientations: PlanogramItemOrientation[];
  merchandisingStyle: "UNIT" | "TRAY" | "CASE";
  /** Legacy metadata only; overhang eligibility always uses the current merchandising style. */
  overhangAsUnit?: boolean;
  shelfType?: ItemShelfType;
  unitWidth: number;
  unitHeight: number;
  unitDepth: number;
  trayWidth: number;
  trayHeight: number;
  trayDepth: number;
  caseWidth: number;
  caseHeight: number;
  caseDepth: number;
  displayWidth: number;
  displayHeight: number;
  displayDepth: number;
  dividerWidth: number;
  dividerHeight: number;
  noOfUnitsInCase: number;
  unitsCaseWide: number;
  unitsCaseDeep: number;
  unitsCaseHigh: number;
  noOfUnitsInTray: number;
  unitsTrayWide: number;
  unitsTrayDeep: number;
  unitsTrayHigh: number;
  squeezeWidthPct: number;
  squeezeHeightPct: number;
  squeezeDepthPct: number;
  squeezeWidth: boolean;
  squeezeHeight: boolean;
  squeezeDepth: boolean;
  overhangSqueezeDepthFlag: boolean;
  cdt1: string;
  cdt1Priority: number;
  cdt2: string;
  cdt2Priority: number;
  cdt3: string;
  cdt3Priority: number;
  facings: number;
  minFacings: number;
  maxFacings: number;
  facingsRows: number;
  minFacingsRows: number;
  maxFacingsRows: number;
  /** See PlanogramItemCommonNonCircular. Non-optional in class instances (default: false). */
  maxFacingsRowsStated: boolean;
  /** See PlanogramItemCommonNonCircular. Non-optional in class instances (default: 0). */
  nestingWidth: number;
  /** See PlanogramItemCommonNonCircular. Non-optional in class instances (default: 0). */
  nestingHeight: number;
  /** See PlanogramItemCommonNonCircular. Non-optional in class instances (default: 0). */
  nestingDepth: number;
  /** See PlanogramItemCommonNonCircular. Always null unless the source planogram stated a depth-facing count. */
  unitsDeep: number | null;
  dividerNeed: ItemDividerNeed | null;
  loaderNeed: ItemLoaderNeed | null;
  minOnShelf: number;
  minDaysOfSupply: number;
  maxDaysOfSupply: number;
  zCappingFlag: boolean;
  /** See PlanogramItemCommonNonCircular. Null unless pogger stated a placed loose-unit count. */
  zCappedUnits: number | null;
  /** See PlanogramItemCommonNonCircular. Null unless pogger stated the cap's units. */
  xCappedUnits: number | null;
  yCappedUnits: number | null;
  /** The cap geometry each saved count was counted on (see `savedCapUnits`). */
  private readonly xCappedOn: string;
  private readonly yCappedOn: string;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  xCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  xCapOrientation: PlanogramItemOrientation;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  yCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  yCapOrientation: PlanogramItemOrientation;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  zCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  zCapOrientation: PlanogramItemOrientation;
  updateStatus?: PlanogramItemUpdateStatus;
  inCoreRange: boolean;
  newItemFlag: boolean;
  newItemStoreCodes?: string[] | undefined;
  dndFlag: boolean;
  spaceMix: number;
  rangedInBiggerOrSmallerStores?:
    | { approvedSmaller: number; allSmaller: number; approvedBigger: number; allBigger: number }
    | undefined;
  ranking: number;

  constructor(item: PlanogramItemCommonNonCircular) {
    this.uniqueId = item.uniqueId;
    this.id = item.id;
    this.productCode = item.productCode;
    this.name = item.name;
    this.upc = item.upc;
    this.brand = item.brand;
    this.variant = item.variant;
    this.categoryCode = item.categoryCode;
    this.subCategory = item.subCategory;
    this.size = item.size;
    this.unitWeight = item.unitWeight ?? null;
    this.trayWeight = item.trayWeight ?? null;
    this.caseWeight = item.caseWeight ?? null;
    this.color = item.color;
    // Normalised to null rather than left undefined: an absent key and a
    // stated null both mean "no measurement", and every pog older than this
    // field carries the absent form.
    this.weightUom = item.weightUom ?? null;
    this.profit = item.profit;
    this.quantity = item.quantity;
    this.price = item.price;
    this.salesAmount = item.salesAmount;
    const placementDefaults = normalizePlanogramItemPlacementDefaults(item);
    this.orientation = placementDefaults.orientation;
    this.validOrientations = placementDefaults.validOrientations;
    this.merchandisingStyle = item.merchandisingStyle;
    this.overhangAsUnit = item.overhangAsUnit;
    this.shelfType = item.shelfType;
    this.unitWidth = item.unitWidth;
    this.unitHeight = item.unitHeight;
    this.unitDepth = item.unitDepth;
    this.trayWidth = item.trayWidth;
    this.trayHeight = item.trayHeight;
    this.trayDepth = item.trayDepth;
    this.caseWidth = item.caseWidth;
    this.caseHeight = item.caseHeight;
    this.caseDepth = item.caseDepth;
    this.displayWidth = item.displayWidth;
    this.displayHeight = item.displayHeight;
    this.displayDepth = item.displayDepth;
    this.dividerWidth = item.dividerWidth;
    this.dividerHeight = item.dividerHeight;
    this.noOfUnitsInCase = item.noOfUnitsInCase;
    this.unitsCaseWide = item.unitsCaseWide;
    this.unitsCaseDeep = item.unitsCaseDeep;
    this.unitsCaseHigh = item.unitsCaseHigh;
    this.noOfUnitsInTray = item.noOfUnitsInTray;
    this.unitsTrayWide = item.unitsTrayWide;
    this.unitsTrayDeep = item.unitsTrayDeep;
    this.unitsTrayHigh = item.unitsTrayHigh;
    this.squeezeWidthPct = item.squeezeWidthPct;
    this.squeezeHeightPct = item.squeezeHeightPct;
    this.squeezeDepthPct = item.squeezeDepthPct;
    this.squeezeWidth = item.squeezeWidth;
    this.squeezeHeight = item.squeezeHeight;
    this.squeezeDepth = item.squeezeDepth;
    this.overhangSqueezeDepthFlag = item.overhangSqueezeDepthFlag;
    this.cdt1 = item.cdt1;
    this.cdt1Priority = item.cdt1Priority;
    this.cdt2 = item.cdt2;
    this.cdt2Priority = item.cdt2Priority;
    this.cdt3 = item.cdt3;
    this.cdt3Priority = item.cdt3Priority;
    this.facingsRows = placementDefaults.facingsRows;
    this.facings = placementDefaults.facings;
    this.minFacings = item.minFacings;
    this.maxFacings = item.maxFacings;
    this.minFacingsRows = item.minFacingsRows;
    this.maxFacingsRows = item.maxFacingsRows;
    this.maxFacingsRowsStated = item.maxFacingsRowsStated ?? false;
    this.nestingWidth = item.nestingWidth ?? 0;
    this.nestingHeight = item.nestingHeight ?? 0;
    this.nestingDepth = item.nestingDepth ?? 0;
    this.unitsDeep = item.unitsDeep ?? null;
    this.dividerNeed = item.dividerNeed ? { ...item.dividerNeed } : null;
    this.loaderNeed = item.loaderNeed ? { ...item.loaderNeed } : null;
    this.minOnShelf = item.minOnShelf;
    this.minDaysOfSupply = item.minDaysOfSupply;
    this.maxDaysOfSupply = item.maxDaysOfSupply;
    this.zCappingFlag = item.zCappingFlag;
    this.zCappedUnits = item.zCappedUnits ?? null;
    this.xCappedUnits = item.xCappedUnits ?? null;
    this.yCappedUnits = item.yCappedUnits ?? null;
    this.xCapNum = item.xCapNum ?? 0;
    this.xCapOrientation = item.xCapOrientation ?? "FRONT";
    this.yCapNum = item.yCapNum ?? 0;
    this.yCapOrientation = item.yCapOrientation ?? "FRONT";
    this.zCapNum = item.zCapNum ?? 0;
    this.zCapOrientation = item.zCapOrientation ?? "FRONT";
    this.updateStatus = item.updateStatus ? { ...item.updateStatus } : undefined;
    this.inCoreRange = item.inCoreRange;
    this.newItemFlag = item.newItemFlag;
    this.newItemStoreCodes = item.newItemStoreCodes ? [...item.newItemStoreCodes] : undefined;
    this.dndFlag = item.dndFlag;
    this.spaceMix = item.spaceMix;
    this.rangedInBiggerOrSmallerStores = item.rangedInBiggerOrSmallerStores
      ? { ...item.rangedInBiggerOrSmallerStores }
      : undefined;
    this.ranking = item.ranking;
    this.xCappedOn = capGeometryOf(this, "x");
    this.yCappedOn = capGeometryOf(this, "y");
  }

  /** Pogger's saved cap units while the cap is as it counted it; null once the cap is edited. */
  savedCapUnits = (axis: "x" | "y") => {
    const saved = axis === "x" ? this.xCappedUnits : this.yCappedUnits;
    const countedOn = axis === "x" ? this.xCappedOn : this.yCappedOn;
    return saved != null && countedOn === capGeometryOf(this, axis) ? saved : null;
  };

  getWidth = () => {
    return this.getOrientedDimensionForOrientation("width", this.orientation);
  };

  getHeight = () => {
    return this.getOrientedDimensionForOrientation("height", this.orientation);
  };

  getDepth = () => {
    return this.getOrientedDimensionForOrientation("depth", this.orientation);
  };

  getHorizontalFacings = () => {
    return Math.ceil(this.facings / this.facingsRows);
  };

  getVerticalFacings = () => {
    return Math.min(this.facings, this.facingsRows);
  };

  getUnits = () => {
    switch (this.merchandisingStyle) {
      case "UNIT":
        return 1;
      case "CASE":
        return this.noOfUnitsInCase;
      case "TRAY":
        return this.noOfUnitsInTray;
    }
  };

  getUnitsWide = () => {
    if (this.merchandisingStyle === "UNIT") {
      return 1;
    }

    const {
      unitsCaseWide,
      unitsCaseHigh,
      unitsCaseDeep,
      unitsTrayWide,
      unitsTrayHigh,
      unitsTrayDeep,
    } = this;

    if (this.merchandisingStyle === "CASE") {
      switch (this.orientation) {
        case "FRONT":
        case "TOP":
          return unitsCaseWide;
        case "FRONT2":
        case "SIDE2":
          return unitsCaseHigh;
        case "SIDE":
        case "TOP2":
          return unitsCaseDeep;
      }
    }

    switch (this.orientation) {
      case "FRONT":
      case "TOP":
        return unitsTrayWide;
      case "FRONT2":
      case "SIDE2":
        return unitsTrayHigh;
      case "SIDE":
      case "TOP2":
        return unitsTrayDeep;
    }
  };

  getUnitsHigh = () => {
    if (this.merchandisingStyle === "UNIT") {
      return 1;
    }

    const {
      unitsCaseWide,
      unitsCaseHigh,
      unitsCaseDeep,
      unitsTrayWide,
      unitsTrayHigh,
      unitsTrayDeep,
    } = this;

    if (this.merchandisingStyle === "CASE") {
      switch (this.orientation) {
        case "FRONT":
        case "SIDE":
          return unitsCaseHigh;
        case "FRONT2":
        case "TOP2":
          return unitsCaseWide;
        case "SIDE2":
        case "TOP":
          return unitsCaseDeep;
      }
    }

    switch (this.orientation) {
      case "FRONT":
      case "SIDE":
        return unitsTrayHigh;
      case "FRONT2":
      case "TOP2":
        return unitsTrayWide;
      case "SIDE2":
      case "TOP":
        return unitsTrayDeep;
    }
  };

  getCumulativeCdt1 = (): string[] => {
    return [this.variant, this.cdt1];
  };

  getCumulativeCdt2 = (): string[] => {
    return [this.variant, this.cdt1, this.cdt2];
  };

  getCumulativeCdt3 = (): string[] => {
    return [this.variant, this.cdt1, this.cdt2, this.cdt3];
  };

  private getSqueezedDimensions = () => getPlanogramItemSqueezedDimensions(this);

  private squeezeItemDimensionResult = (
    result: number,
    squeeze: boolean,
    squeezePercentage: number,
  ) => {
    return squeeze ? result * squeezePercentage : result;
  };

  /** The merchandised box turned into `orientation`, via the shared axis table. */
  getOrientedDimensionForOrientation = (
    dimension: "width" | "height" | "depth",
    orientation: PlanogramItemOrientation,
  ): number => getOrientedDimensions(this.getSqueezedDimensions(), orientation)[dimension];

  /** The loose consumer unit turned into `orientation`: z-caps are loose units, not more packs. */
  getOrientedUnitDimensionForOrientation = (
    dimension: "width" | "height" | "depth",
    orientation: PlanogramItemOrientation,
  ): number =>
    getOrientedDimensions(
      {
        width: this.squeezeItemDimensionResult(
          this.unitWidth,
          this.squeezeWidth,
          this.squeezeWidthPct,
        ),
        height: this.squeezeItemDimensionResult(
          this.unitHeight,
          this.squeezeHeight,
          this.squeezeHeightPct,
        ),
        depth: this.squeezeItemDimensionResult(
          this.unitDepth,
          this.squeezeDepth,
          this.squeezeDepthPct,
        ),
      },
      orientation,
    )[dimension];

  /**
   * The stacked block, caps excluded. Nesting is deliberately not applied: real
   * files draw nested stacks at rows × height, and the PSA convention is unconfirmed.
   */
  getStackHeight = (): number => this.getVerticalFacings() * this.getHeight();

  /** The width mirror of {@link getStackHeight}, x-caps excluded; nesting likewise not applied. */
  getBlockWidth = (): number => this.getHorizontalFacings() * this.getWidth();

  /** The whole vertical footprint of the position: the stack plus the caps laid on it. */
  getBlockHeight = (): number => this.getStackHeight() + this.getYCappingHeight();

  /**
   * The tallest stack fitting under `clearance`, capped by `maxFacingsRows`
   * (rows; caps sit on top and are not counted against it). Pure, so the editor can ask
   * before committing a change. One row is the floor, for the UI to flag.
   */
  getMaxRowsForClearance = (clearance: number): number => {
    const height = this.getHeight();
    const caps = this.getYCappingHeight();
    const ceiling = Math.max(1, Math.floor(this.maxFacingsRows) || 1);

    let rows = 1;
    while (rows + 1 <= ceiling && (rows + 1) * height + caps <= clearance) {
      rows += 1;
    }
    return rows;
  };

  /** An x- or y-cap is the position's own merchandised box turned, not a loose unit (unlike z-caps). */
  getXCappingWidth = (): number => {
    if (this.xCapNum <= 0) return 0;
    return this.xCapNum * this.getOrientedDimensionForOrientation("width", this.xCapOrientation);
  };

  getYCappingHeight = (): number => {
    if (this.yCapNum <= 0) return 0;
    return this.yCapNum * this.getOrientedDimensionForOrientation("height", this.yCapOrientation);
  };

  /** Unlike the x- and y-caps, z-caps are measured on the consumer unit. */
  getZCappingDepth = (): number => {
    if (this.zCapNum <= 0) return 0;
    return (
      this.zCapNum * this.getOrientedUnitDimensionForOrientation("depth", this.zCapOrientation)
    );
  };
}

export interface PlanogramItemNonCircular extends PlanogramItemCommonNonCircular {
  shelf: string | null;
}

export class PlanogramItem extends PlanogramItemCommon {
  shelf: PlanogramShelf;

  constructor(item: PlanogramItemCommonNonCircular, shelf: PlanogramShelf) {
    super(item);
    this.shelf = shelf;
  }

  /**
   * Units deep for this placement. The source planogram's stated count
   * (`unitsDeep`) wins when present; otherwise the geometric guess of how
   * many fit behind each other on this shelf. This is the ONLY place that
   * guess is written — getCapacity() and getUnitsOnShelf() must go through
   * here, so a stated unitsDeep reaches every derived number.
   */
  getDepthFacings = () => {
    if (this.unitsDeep !== null && this.unitsDeep !== undefined) return this.unitsDeep;
    const depth = this.getDepth();
    const shelfDepth = usableShelfDepth(this.shelf);
    const normalCount = Math.floor(shelfDepth / depth);
    if (normalCount > 0) return normalCount;
    return frontOverhangAllowed(this) ? 1 : 0;
  };

  getCoordinates = () => {
    const { xLeft, xRight, yBottom, yTop } = this.getLayout();
    return { xLeft, xRight, yBottom, yTop };
  };

  /**
   * {@link getCoordinates} plus the space the shelf's dividers give this item:
   * `xLeft`/`xRight` bound the product (dividers between its own facings
   * included), `slotXLeft`/`slotXRight` add its leading and trailing dividers.
   */
  getLayout = () => {
    const mergedShelves = this.shelf.getMergedShelves();
    const mergedXLeft = mergedShelves[0].getXLeft();
    const mergedMerchandisableWidth = this.shelf.getMergedMerchandisableWidth();

    // Build ordered list of items across merged shelves (left-to-right)
    const mergedItems: PlanogramItem[] = [];
    for (const shelf of mergedShelves) {
      for (const item of shelf.items) {
        mergedItems.push(item);
      }
    }

    const dividerSlots = getShelfItemDividerSlots(mergedItems);
    const itemWidths = dividerSlots
      ? dividerSlots.map((slot) => slot.width)
      : mergedItems.map(getShelfItemWidth);
    const itemXOffsets = getShelfItemXOffsets({
      itemWidths,
      merchandisableWidth: mergedMerchandisableWidth,
      overflowPolicy: "compress-to-boundary",
      deadSpans: getMergedDeadSpans(mergedShelves),
      pieces: dividerSlots?.map((slot) => slot.piece),
    });

    for (let i = 0; i < mergedItems.length; i++) {
      const shelfItem = mergedItems[i];
      const itemWidth = itemWidths[i];
      const itemHeight = shelfItem.getBlockHeight();

      if (shelfItem === this) {
        const slotXLeft = mergedXLeft + itemXOffsets[i];
        const slot = dividerSlots?.[i];
        const xLeft = slot ? slotXLeft + slot.productX : slotXLeft;
        return {
          xLeft,
          xRight: slot ? xLeft + slot.productWidth : xLeft + itemWidth,
          yBottom: this.shelf.getYBottom() + this.shelf.thickness,
          yTop: this.shelf.getYBottom() + this.shelf.thickness + itemHeight,
          slotXLeft,
          slotXRight: slotXLeft + itemWidth,
          columnXs: slot
            ? slot.columnXs.map((x) => slotXLeft + x)
            : Array.from(
                { length: this.getHorizontalFacings() },
                (_, column) => xLeft + column * this.getWidth(),
              ),
          dividerXs: slot ? slot.dividerXs.map((x) => slotXLeft + x) : [],
          dividers: slot?.dividers ?? null,
        };
      }
    }

    // This shouldn't happen.
    throw new Error(`Item ${this.uniqueId} not found on merged shelves`);
  };

  /** Caps are whole packs, so cases on shelf stay units on shelf over units per pack. */
  getUnitsOnShelf = () =>
    this.facings * this.getDepthFacings() * this.getUnits() + this.getCapUnits();

  /**
   * Pogger's own cap units win, as for z caps: a weight trim may have shortened a cap. Otherwise
   * counted as pogger plans caps: turned packs standing under the stack, laid packs covering the
   * block, each as deep as the shelf takes. Real files leave caps out of their unit totals.
   */
  getCapUnits = () => {
    if (this.xCapNum <= 0 && this.yCapNum <= 0) return 0;
    const depth = usableShelfDepth(this.shelf);
    const yPacks =
      this.yCapNum *
      this.capPacks("width", this.getBlockWidth(), this.yCapOrientation) *
      this.capPacks("depth", depth, this.yCapOrientation);
    const xUnits = this.xCapNum > 0 ? (this.savedCapUnits("x") ?? this.getXCapUnits(depth)) : 0;
    const yUnits = this.yCapNum > 0 ? (this.savedCapUnits("y") ?? yPacks * this.getUnits()) : 0;
    return xUnits + yUnits;
  };

  /** Turned packs in the x-cap: each column filled under the stack, as a PSA x-cap stands. */
  getXCapFacings = () =>
    this.xCapNum <= 0
      ? 0
      : this.xCapNum * this.capPacks("height", this.getStackHeight(), this.xCapOrientation);

  /** Units in `facings` x-cap packs, each as deep as `depth` takes. */
  getXCapUnits = (depth = usableShelfDepth(this.shelf), facings = this.getXCapFacings()) =>
    facings * this.capPacks("depth", depth, this.xCapOrientation) * this.getUnits();

  private capPacks = (
    dimension: "width" | "height" | "depth",
    span: number,
    orientation: PlanogramItemOrientation,
  ) => fitAtLeastOne(span, this.getOrientedDimensionForOrientation(dimension, orientation));

  getCapacity = () => {
    const plannedUnits = this.getUnitsOnShelf();
    if (!this.zCappingFlag) return plannedUnits;

    // Loose units behind the stock. `zCappedUnits` is pogger's actual placed
    // total (already across facings) and wins when present; otherwise derive it
    // geometrically from the stock's own breakdown and the leftover depth — the
    // base-pog fill path pogger did not annotate.
    const looseUnits =
      this.zCappedUnits ?? this.facings * this.getZCappedUnitsPerLayer() * this.getZCappedLayers();

    return plannedUnits + looseUnits;
  };

  /**
   * Loose units per z-cap layer: the pack's own breakdown when the cap faces the
   * same way, else as many turned units as fit across the drawn face.
   */
  getZCappedUnitsPerLayer = () => {
    if (this.zCapOrientation === this.orientation) return this.getUnitsWide() * this.getUnitsHigh();
    return (
      fitAtLeastOne(
        this.getWidth(),
        this.getOrientedUnitDimensionForOrientation("width", this.zCapOrientation),
      ) *
      fitAtLeastOne(
        this.getHeight(),
        this.getOrientedUnitDimensionForOrientation("height", this.zCapOrientation),
      )
    );
  };

  /**
   * Loose layers behind the planned stock: a stated zCapNum, so capacity agrees
   * with getZCappingDepth; else as many as the leftover depth takes.
   */
  getZCappedLayers = () => {
    if (this.zCapNum > 0) return this.zCapNum;
    const looseUnitDepth = this.getOrientedUnitDimensionForOrientation(
      "depth",
      this.zCapOrientation,
    );
    if (looseUnitDepth <= 0) return 0;
    return Math.max(
      0,
      Math.floor((this.shelf.depth - this.getDepth() * this.getDepthFacings()) / looseUnitDepth),
    );
  };

  getDaysOfSupply = () => {
    return (this.getCapacity() / this.quantity) * 7;
  };

  getDaysOfSupplyPercentage = () => {
    return (this.getDaysOfSupply() / this.minDaysOfSupply) * 100;
  };

  getMOS = () => {
    return this.getCapacity() / this.minOnShelf;
  };

  getMOSPercentage = () => {
    return this.getMOS() * 100;
  };

  getCasesOnShelf = () => {
    if (this.merchandisingStyle === "UNIT") return null;
    const uos = this.getUnitsOnShelf();

    if (!uos) return null;
    return Math.round(uos / this.getUnits());
  };

  duplicate = (shelf: PlanogramShelf, uniqueId = uuid()) => {
    return new PlanogramItem(
      {
        ...adapterPlanogramItemToPlanogramItemCommon(this),
        uniqueId,
        dndFlag: true,
      },
      shelf,
    );
  };

  unrange = () => {
    const unrangedItem = new UnrangedPlanogramItem(
      {
        ...this,
        potentialShelves: [this.shelf.uniqueId],
        reasons: [],
      },
      this.shelf.bay.planogram.pog,
    );

    // New object into unranged items.
    this.shelf.bay.planogram.pog.unrangedItems.unshift(unrangedItem);

    // Remove all matching products from planogram.
    for (const bay of this.shelf.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        shelf.items = shelf.items.filter((item) => item.productCode !== this.productCode);
      }
    }

    return unrangedItem;
  };

  rangeItem = (rangedItem: UnrangedPlanogramItem | PlanogramItem, side: string) => {
    this.rangeItems([rangedItem], side);
  };

  rangeItems = (rangedItems: (UnrangedPlanogramItem | PlanogramItem)[], side: string) => {
    // Ranged items need to be looped in reverse order
    for (let i = rangedItems.length - 1; i >= 0; i--) {
      const rangedItem = rangedItems[i];
      let finalRangedItem: PlanogramItem;

      // Planogram item
      if ("shelf" in rangedItem) {
        rangedItem.shelf.items = rangedItem.shelf.items.filter(
          (item) => item.uniqueId !== rangedItem.uniqueId,
        );
        finalRangedItem = rangedItem;
        finalRangedItem.shelf = this.shelf;

        // Unranged item
      } else {
        rangedItem.pog.unrangedItems = rangedItem.pog.unrangedItems.filter(
          (unrangedItem) => unrangedItem !== rangedItem,
        );
        finalRangedItem = new PlanogramItem(rangedItem, this.shelf);
      }

      finalRangedItem.dndFlag = true;
      const thisItemIndex = this.shelf.items.findIndex((item) => item === this);
      this.shelf.items.splice(thisItemIndex + (side === "right" ? 1 : 0), 0, finalRangedItem);
    }
  };

  delete = (storeCodes: string[]) => {
    this.shelf.bay.planogram.pog.deletedItems.push({
      uniqueId: this.uniqueId,
      id: this.id,
      productCode: this.productCode,
      name: this.name,
      upc: this.upc,
      brand: this.brand,
      variant: this.variant,
      categoryCode: this.categoryCode,
      subCategory: this.subCategory,
      size: this.size,
      color: this.color,
      profit: this.profit,
      quantity: this.quantity,
      price: this.price,
      salesAmount: this.salesAmount,
      orientation: this.orientation,
      validOrientations: [...this.validOrientations],
      merchandisingStyle: this.merchandisingStyle,
      overhangAsUnit: this.overhangAsUnit,
      unitWidth: this.unitWidth,
      unitHeight: this.unitHeight,
      unitDepth: this.unitDepth,
      trayWidth: this.trayWidth,
      trayHeight: this.trayHeight,
      trayDepth: this.trayDepth,
      caseWidth: this.caseWidth,
      caseHeight: this.caseHeight,
      caseDepth: this.caseDepth,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      displayDepth: this.displayDepth,
      dividerWidth: this.dividerWidth,
      dividerHeight: this.dividerHeight,
      noOfUnitsInCase: this.noOfUnitsInCase,
      unitsCaseWide: this.unitsCaseWide,
      unitsCaseDeep: this.unitsCaseDeep,
      unitsCaseHigh: this.unitsCaseHigh,
      noOfUnitsInTray: this.noOfUnitsInTray,
      unitsTrayWide: this.unitsTrayWide,
      unitsTrayDeep: this.unitsTrayDeep,
      unitsTrayHigh: this.unitsTrayHigh,
      squeezeWidthPct: this.squeezeWidthPct,
      squeezeHeightPct: this.squeezeHeightPct,
      squeezeDepthPct: this.squeezeDepthPct,
      squeezeWidth: this.squeezeWidth,
      squeezeHeight: this.squeezeHeight,
      squeezeDepth: this.squeezeDepth,
      overhangSqueezeDepthFlag: this.overhangSqueezeDepthFlag,
      cdt1: this.cdt1,
      cdt1Priority: this.cdt1Priority,
      cdt2: this.cdt2,
      cdt2Priority: this.cdt2Priority,
      cdt3: this.cdt3,
      cdt3Priority: this.cdt3Priority,
      facings: this.facings,
      minFacings: this.minFacings,
      maxFacings: this.maxFacings,
      facingsRows: this.facingsRows,
      minFacingsRows: this.minFacingsRows,
      maxFacingsRows: this.maxFacingsRows,
      maxFacingsRowsStated: this.maxFacingsRowsStated,
      nestingWidth: this.nestingWidth,
      nestingHeight: this.nestingHeight,
      nestingDepth: this.nestingDepth,
      unitsDeep: this.unitsDeep,
      minOnShelf: this.minOnShelf,
      minDaysOfSupply: this.minDaysOfSupply,
      maxDaysOfSupply: this.maxDaysOfSupply,
      zCappingFlag: this.zCappingFlag,
      zCappedUnits: this.zCappedUnits,
      xCappedUnits: this.savedCapUnits("x"),
      yCappedUnits: this.savedCapUnits("y"),
      inCoreRange: this.inCoreRange,
      newItemFlag: this.newItemFlag,
      dndFlag: this.dndFlag,
      spaceMix: this.spaceMix,
      ranking: this.ranking,
      storeCodes,
    });
    this.shelf.items = this.shelf.items.filter((item) => item.uniqueId !== this.uniqueId);
  };
}

export interface UnrangedPlanogramItemNonCircular extends PlanogramItemNonCircular {
  potentialShelves: string[];
  reasons: string[];
}

export class UnrangedPlanogramItem extends PlanogramItemCommon {
  potentialShelves: string[];
  reasons: string[];
  pog: Pog;

  constructor(item: Omit<UnrangedPlanogramItemNonCircular, "shelf">, pog: Pog) {
    super(item);
    this.potentialShelves = [...item.potentialShelves];
    this.reasons = [...item.reasons];
    this.pog = pog;
  }

  range = () => {
    // Remove from unranged items.
    this.pog.unrangedItems = this.pog.unrangedItems.filter(
      (item) => item.productCode !== this.productCode,
    );

    // Add to shelf.
    for (const potentialShelf of this.potentialShelves) {
      const shelf = this.pog.planogram.getShelf(potentialShelf);
      if (shelf) {
        shelf.addItems([this]);
        break;
      }
    }
  };

  delete = (storeCodes: string[]) => {
    this.pog.deletedItems.push({
      uniqueId: this.uniqueId,
      id: this.id,
      productCode: this.productCode,
      name: this.name,
      upc: this.upc,
      brand: this.brand,
      variant: this.variant,
      categoryCode: this.categoryCode,
      subCategory: this.subCategory,
      size: this.size,
      color: this.color,
      profit: this.profit,
      quantity: this.quantity,
      price: this.price,
      salesAmount: this.salesAmount,
      orientation: this.orientation,
      validOrientations: [...this.validOrientations],
      merchandisingStyle: this.merchandisingStyle,
      overhangAsUnit: this.overhangAsUnit,
      unitWidth: this.unitWidth,
      unitHeight: this.unitHeight,
      unitDepth: this.unitDepth,
      trayWidth: this.trayWidth,
      trayHeight: this.trayHeight,
      trayDepth: this.trayDepth,
      caseWidth: this.caseWidth,
      caseHeight: this.caseHeight,
      caseDepth: this.caseDepth,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      displayDepth: this.displayDepth,
      dividerWidth: this.dividerWidth,
      dividerHeight: this.dividerHeight,
      noOfUnitsInCase: this.noOfUnitsInCase,
      unitsCaseWide: this.unitsCaseWide,
      unitsCaseDeep: this.unitsCaseDeep,
      unitsCaseHigh: this.unitsCaseHigh,
      noOfUnitsInTray: this.noOfUnitsInTray,
      unitsTrayWide: this.unitsTrayWide,
      unitsTrayDeep: this.unitsTrayDeep,
      unitsTrayHigh: this.unitsTrayHigh,
      squeezeWidthPct: this.squeezeWidthPct,
      squeezeHeightPct: this.squeezeHeightPct,
      squeezeDepthPct: this.squeezeDepthPct,
      squeezeWidth: this.squeezeWidth,
      squeezeHeight: this.squeezeHeight,
      squeezeDepth: this.squeezeDepth,
      overhangSqueezeDepthFlag: this.overhangSqueezeDepthFlag,
      cdt1: this.cdt1,
      cdt1Priority: this.cdt1Priority,
      cdt2: this.cdt2,
      cdt2Priority: this.cdt2Priority,
      cdt3: this.cdt3,
      cdt3Priority: this.cdt3Priority,
      facings: this.facings,
      minFacings: this.minFacings,
      maxFacings: this.maxFacings,
      facingsRows: this.facingsRows,
      minFacingsRows: this.minFacingsRows,
      maxFacingsRows: this.maxFacingsRows,
      maxFacingsRowsStated: this.maxFacingsRowsStated,
      nestingWidth: this.nestingWidth,
      nestingHeight: this.nestingHeight,
      nestingDepth: this.nestingDepth,
      unitsDeep: this.unitsDeep,
      minOnShelf: this.minOnShelf,
      minDaysOfSupply: this.minDaysOfSupply,
      maxDaysOfSupply: this.maxDaysOfSupply,
      zCappingFlag: this.zCappingFlag,
      zCappedUnits: this.zCappedUnits,
      xCappedUnits: this.savedCapUnits("x"),
      yCappedUnits: this.savedCapUnits("y"),
      inCoreRange: this.inCoreRange,
      newItemFlag: this.newItemFlag,
      dndFlag: this.dndFlag,
      spaceMix: this.spaceMix,
      ranking: this.ranking,
      storeCodes,
      rangedInBiggerOrSmallerStores: this.rangedInBiggerOrSmallerStores
        ? { ...this.rangedInBiggerOrSmallerStores }
        : undefined,
    });
    this.pog.unrangedItems = this.pog.unrangedItems.filter(
      (unrangedItem) => unrangedItem.uniqueId !== this.uniqueId,
    );
  };
}
